import React, { Fragment, useCallback, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { compose, defaultProps, withProps } from 'recompose';
import { get, map } from 'lodash';
import { Crumb } from 'components/atoms';

import './BreadCrumb.scss';

function ensureTrailingSlash(url) {
	return url && !url.endsWith('/') ? `${url}/` : url;
}

function isAbsoluteUrl(raw) {
	return /^https?:\/\//i.test(raw);
}

function joinUrl(base, path) {
	if (!base) return path || '';
	if (!path) return base;
	return `${base.replace(/\/+$/, '')}/${path.replace(/^\/+/, '')}`;
}

function getCrumbHref(crumb) {
	const raw = (
		get(crumb, 'props.href') ||
		get(crumb, 'props.to') ||
		get(crumb, 'props.id') ||
		''
	).toString();
	return raw.split('?').shift();
}

function createCLPBreadcrumbSchema(crumbs = [], baseUrl) {
	const ROOT_URL = baseUrl || 'https://www.selfstorage.com';

	const filtered = crumbs.filter((crumb) => crumb?.props?.label);

	const itemListElement = filtered.map((crumb, index) => {
		const rawHref = (get(crumb, 'props.href', '') || '').split('?').shift();
		const absoluteUrl = !rawHref
			? ROOT_URL
			: isAbsoluteUrl(rawHref)
				? rawHref
				: `${ROOT_URL.replace(/\/$/, '')}${
						rawHref.startsWith('/') ? rawHref : `/${rawHref}`
					}`;
		const idUrl = ensureTrailingSlash(absoluteUrl);

		return {
			'@type': 'ListItem',
			position: index + 1,
			item: {
				'@id': idUrl,
				name: crumb.props.name || crumb.props.label,
				url: idUrl,
			},
		};
	});

	const cityCrumb = filtered.find(
		(c) => c?.props?.className === 'sublocality',
	);
	let landingUrl = '';

	if (cityCrumb) {
		const raw = (get(cityCrumb, 'props.href', '') || '').split('?').shift();
		const abs = !raw
			? ROOT_URL
			: isAbsoluteUrl(raw)
				? raw
				: `${ROOT_URL.replace(/\/$/, '')}${raw.startsWith('/') ? raw : `/${raw}`}`;
		landingUrl = ensureTrailingSlash(abs);
	} else {
		landingUrl =
			get(
				itemListElement,
				`[${itemListElement.length - 1}].item['@id']`,
				'',
			) || ensureTrailingSlash(ROOT_URL);
	}

	const breadcrumbListId = `${landingUrl.replace(/#.*$/, '')}#breadcrumb`;
	return {
		'@type': 'BreadcrumbList',
		'@id': breadcrumbListId,
		itemListElement,
	};
}

function createCrumbSchema(crumbs) {
	return {
		'@context': 'http://schema.org',
		'@type': 'BreadcrumbList',
		itemListElement: crumbs
			.filter((crumb) => crumb.props.label)
			.map((crumb, index) => ({
				'@type': 'ListItem',
				position: index + 1,
				item: {
					'@id': get(crumb, 'props.id', get(crumb, 'props.to', ''))
						.split('?')
						.shift(),
					name: crumb.props.name || crumb.props.label,
				},
			})),
	};
}

/** ---------- HOC to build crumbs ---------- */
export const enhance = compose(
	defaultProps({ location: {}, searchType: 'storage' }),
	withProps(
		({
			location,
			crumbs = [],
			cityUrl,
			stateUrl,
			baseUrl,
			tertiary,
			isCLPPage,
		}) => {
			const newCrumbs = [];
			const { fullState, subLocality, city } = location || {};
			const sub = subLocality || city;

			newCrumbs.push(
				<Crumb
					href={`${baseUrl}`}
					id={`${baseUrl}`}
					key="crumb-home"
					label="Home"
					name="Home"
					className="state custom"
					baseUrl={baseUrl}
					tertiary={tertiary}
				/>,
			);

			newCrumbs.push(
				<Crumb
					href={joinUrl(baseUrl, '/storage-units-near-me')}
					id={joinUrl(baseUrl, '/storage-units-near-me')}
					key="crumb-find-storage"
					label="Find storage"
					name="Find storage"
					className="custom"
				/>,
			);

			if (stateUrl) {
				newCrumbs.push(
					<Crumb
						key={`crumb-state-${(fullState || stateUrl || '').toString()}`}
						id={joinUrl(baseUrl, stateUrl)}
						href={joinUrl(baseUrl, stateUrl)}
						isLast={!cityUrl && !crumbs.length}
						label={fullState}
						name={fullState}
						className="state custom"
						tertiary={tertiary}
					/>,
				);
			}

			if (cityUrl) {
				const isLast = !crumbs.length;
				const cityName = ((sub ?? cityUrl) || '').toString().trim();
				const hasLocation = fullState && cityName;
				const label = cityName
					? `${cityName} Self Storage`
					: 'Self Storage';

				newCrumbs.push(
					<Crumb
						key={`crumb-city-${cityName}`}
						id={joinUrl(baseUrl, cityUrl)}
						className="sublocality custom"
						href={joinUrl(baseUrl, cityUrl)}
						isLast={isLast}
						label={label}
						name={
							hasLocation
								? `Self storage in ${fullState}, ${cityName}`
								: 'Self Storage'
						}
					>
						{label}
					</Crumb>,
				);
			}

			const fullCrumbs = [...newCrumbs, ...crumbs];
			let fullCrumbsSchema = fullCrumbs;
			if (crumbs[crumbs.length - 1]?.key === 'facility') {
				fullCrumbsSchema = fullCrumbs.filter((_, index) =>
					[0, 3, 4].includes(index),
				);
			}

			return {
				crumbs: fullCrumbs,
				crumbJSON: isCLPPage
					? createCLPBreadcrumbSchema(fullCrumbs, baseUrl)
					: createCrumbSchema(fullCrumbsSchema),
				baseUrl,
			};
		},
	),
);

/** ---------- Component ---------- */
export function Breadcrumbs({ crumbs, crumbJSON, className, baseUrl }) {
	// Compute a sensible mobile back href
	const backHref = useMemo(() => {
		const items = Array.isArray(crumbs) ? crumbs : [];
		if (items.length >= 2) {
			const prev = items[items.length - 2];
			const href = getCrumbHref(prev);
			if (href) return href;
		}
		const firstWithHref = items.find((c) => !!getCrumbHref(c));
		if (firstWithHref) return getCrumbHref(firstWithHref);
		return baseUrl || '/';
	}, [crumbs, baseUrl]);

	const onMobileBackClick = useCallback((e) => {
		if (
			typeof window !== 'undefined' &&
			window.history &&
			window.history.length > 1
		) {
			e.preventDefault();
			window.history.back();
		}
	}, []);

	return (
		<Fragment>
			{/* Desktop/Tablet full breadcrumb */}
			<ul
				className={classnames('ss-breadcrumbs', className)}
				id="breadcrumb"
			>
				{map(crumbs, (crumb) => (
					<li
						className="ss-breadcrumb"
						key={crumb.key}
					>
						{crumb}
					</li>
				))}
			</ul>

			{/* Mobile-only Back control */}
			<a
				href={backHref}
				className="ss-breadcrumbs__mobile-back"
				onClick={onMobileBackClick}
				aria-label="Go back"
			>
				<span
					aria-hidden="true"
					className="ss-breadcrumbs__chevron"
				/>
				<span className="ss-breadcrumbs__back-text">Back</span>
			</a>

			<Helmet>
				<script type="application/ld+json">
					{JSON.stringify(crumbJSON)}
					{/* {JSON.stringify(crumbJSON.itemListElement.filter((_, index) => [0, 3, 4].includes(index)))} */}
				</script>
			</Helmet>
		</Fragment>
	);
}

Breadcrumbs.propTypes = {
	className: PropTypes.string,
	crumbJSON: PropTypes.object,
	crumbs: PropTypes.arrayOf(PropTypes.node),
	baseUrl: PropTypes.string,
};

export default enhance(Breadcrumbs);
